/**
 * LICITARS 3.0 - Process Details Accordion V3
 * Navy/Silver themed accordion for process details
 */

import React from "react";
import {
  Building2,
  DollarSign,
  Calendar,
  User,
  Hash,
  FileText,
  Clock,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Loader2,
  Flag,
} from "lucide-react";

interface ProcessDetailsAccordionV3Props {
  process: any;
}

export const ProcessDetailsAccordionV3: React.FC<ProcessDetailsAccordionV3Props> = ({
  process,
}) => {
  if (!process) return null;

  const getStatusConfig = (status: string) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case "active":
      case "ativo":
        return {
          icon: CheckCircle2,
          bg: "bg-green-500/20",
          text: "text-green-300",
          label: "Ativo",
        };
      case "completed":
      case "concluído":
        return {
          icon: CheckCircle2,
          bg: "bg-blue-500/20",
          text: "text-blue-300",
          label: "Concluído",
        };
      case "pending":
      case "pendente":
        return {
          icon: Loader2,
          bg: "bg-yellow-500/20",
          text: "text-yellow-300",
          label: "Pendente",
        };
      case "cancelled":
      case "cancelado":
        return {
          icon: XCircle,
          bg: "bg-red-500/20",
          text: "text-red-300",
          label: "Cancelado",
        };
      default:
        return {
          icon: AlertCircle,
          bg: "bg-gray-500/20",
          text: "text-gray-300",
          label: status,
        };
    }
  };

  const formatCurrency = (value: number | string) => {
    if (typeof value === "number") {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(value);
    }
    return value;
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("pt-BR");
  };

  const statusConfig = process.status ? getStatusConfig(process.status) : null;
  const StatusIcon = statusConfig?.icon;

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2 mb-2">
        <Building2 className="w-4 h-4 text-blue-400" />
        <h3 className="text-xs font-bold text-silver uppercase tracking-wider">
          Dados do Processo
        </h3>
      </div>

      <dl className="grid grid-cols-1 gap-2 text-sm">
        <div className="flex flex-col gap-1">
          <dt className="text-xs font-semibold text-silver/70">Nome do Processo:</dt>
          <dd className="text-platinum font-medium">{process.object}</dd>
        </div>

        {process.id && (
          <div className="flex flex-col gap-1">
            <dt className="text-xs font-semibold text-silver/70 flex items-center gap-1">
              <Hash className="w-3 h-3" />
              ID do Processo:
            </dt>
            <dd className="text-silver/90 font-mono text-xs bg-navy-700 px-2 py-1 rounded">
              {process.id}
            </dd>
          </div>
        )}

        {process.description && (
          <div className="flex flex-col gap-1">
            <dt className="text-xs font-semibold text-silver/70">Descrição:</dt>
            <dd className="text-platinum text-sm">{process.description}</dd>
          </div>
        )}

        {process.status && statusConfig && (
          <div className="flex flex-col gap-1">
            <dt className="text-xs font-semibold text-silver/70">Status:</dt>
            <dd>
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded ${statusConfig.bg}`}>
                {StatusIcon && <StatusIcon className={`w-3 h-3 ${statusConfig.text}`} />}
                <span className={`text-xs font-medium ${statusConfig.text}`}>
                  {statusConfig.label}
                </span>
              </div>
            </dd>
          </div>
        )}

        {process.process_type && (
          <div className="flex flex-col gap-1">
            <dt className="text-xs font-semibold text-silver/70">Tipo:</dt>
            <dd className="text-platinum">
              <span className="px-2 py-1 rounded bg-purple-500/20 text-purple-300 text-xs font-medium">
                {process.process_type}
              </span>
            </dd>
          </div>
        )}

        {process.number && (
          <div className="flex flex-col gap-1">
            <dt className="text-xs font-semibold text-silver/70">Número:</dt>
            <dd className="text-platinum font-mono">{process.number}</dd>
          </div>
        )}

        {process.year && (
          <div className="flex flex-col gap-1">
            <dt className="text-xs font-semibold text-silver/70">Ano:</dt>
            <dd className="text-platinum">{process.year}</dd>
          </div>
        )}

        {process.agency && (
          <div className="flex flex-col gap-1">
            <dt className="text-xs font-semibold text-silver/70 flex items-center gap-1">
              <Building2 className="w-3 h-3" />
              Órgão:
            </dt>
            <dd className="text-platinum">{process.agency}</dd>
          </div>
        )}

        {process.contracting_entity && (
          <div className="flex flex-col gap-1">
            <dt className="text-xs font-semibold text-silver/70 flex items-center gap-1">
              <Building2 className="w-3 h-3" />
              Entidade Contratante:
            </dt>
            <dd className="text-platinum">{process.contracting_entity}</dd>
          </div>
        )}

        {process.budget && (
          <div className="flex flex-col gap-1">
            <dt className="text-xs font-semibold text-silver/70 flex items-center gap-1">
              <DollarSign className="w-3 h-3" />
              Orçamento:
            </dt>
            <dd className="text-platinum font-semibold text-green-400">
              {formatCurrency(process.budget)}
            </dd>
          </div>
        )}

        {process.created_at && (
          <div className="flex flex-col gap-1">
            <dt className="text-xs font-semibold text-silver/70 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              Criado em:
            </dt>
            <dd className="text-platinum text-xs">
              {new Date(process.created_at).toLocaleString("pt-BR")}
            </dd>
          </div>
        )}

        {process.updated_at && (
          <div className="flex flex-col gap-1">
            <dt className="text-xs font-semibold text-silver/70 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Atualizado em:
            </dt>
            <dd className="text-platinum text-xs">
              {new Date(process.updated_at).toLocaleString("pt-BR")}
            </dd>
          </div>
        )}

        {process.user && (
          <div className="flex flex-col gap-1">
            <dt className="text-xs font-semibold text-silver/70 flex items-center gap-1">
              <User className="w-3 h-3" />
              Usuário:
            </dt>
            <dd className="text-platinum">
              <div className="flex items-center gap-2">
                <span className="font-medium">{process.user.username}</span>
                {process.user.email && (
                  <span className="text-xs text-silver/60">
                    ({process.user.email})
                  </span>
                )}
              </div>
            </dd>
          </div>
        )}

        {process.observations && (
          <div className="flex flex-col gap-1">
            <dt className="text-xs font-semibold text-silver/70 flex items-center gap-1">
              <FileText className="w-3 h-3" />
              Observações:
            </dt>
            <dd className="text-platinum text-sm bg-navy-700/50 p-2 rounded">
              {process.observations}
            </dd>
          </div>
        )}

        {process.deadline && (
          <div className="flex flex-col gap-1">
            <dt className="text-xs font-semibold text-silver/70 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              Prazo:
            </dt>
            <dd className="text-platinum">
              {formatDate(process.deadline)}
            </dd>
          </div>
        )}

        {process.priority && (
          <div className="flex flex-col gap-1">
            <dt className="text-xs font-semibold text-silver/70 flex items-center gap-1">
              <Flag className="w-3 h-3" />
              Prioridade:
            </dt>
            <dd>
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                process.priority.toLowerCase() === "alta" || process.priority.toLowerCase() === "high"
                  ? "bg-red-500/20 text-red-300"
                  : process.priority.toLowerCase() === "média" || process.priority.toLowerCase() === "medium"
                  ? "bg-yellow-500/20 text-yellow-300"
                  : "bg-green-500/20 text-green-300"
              }`}>
                {process.priority}
              </span>
            </dd>
          </div>
        )}
      </dl>
    </section>
  );
};
